/**
 * phaseContingency — the N-1 dimension of per-phase resource adequacy.
 *
 * WHY THIS EXISTS. {@link assessPhaseFeasibility} grades a phase on an N-0
 * basis: it sums capacity-credit-weighted nameplate and asks whether that
 * covers load plus the planning reserve. That is the right question for
 * "is there enough capacity", but it cannot see the question a data-center
 * sponsor actually has to answer: *what survives the loss of the single
 * largest block*. A phase can read comfortably adequate at N-0 and still
 * collapse the moment one machine trips.
 *
 * The Redhead phase plan is the worked example this module was built from:
 * Phase 2 carries a 400 MW gas-turbine entry against a 350 MW load and
 * grades adequate on the N-0 sum. But 400 MW in ONE block is itself the
 * contingency, so the firm capacity that survives N-1 is whatever is left
 * over (the grid share and the recip backup) and nothing else. The block
 * contributes zero. That defect is invisible to an N-0 check and obvious to
 * this one.
 *
 * BLOCK COUNTS ARE THE HINGE, and the phase plan does not carry them:
 * `componentParams` gives MW per component, not how many machines that MW
 * sits in. So this module defaults to the WORST case (each component in a
 * single block), marks that assumption explicitly on every affected row, and
 * accepts declared counts through `blockCounts`. A promoted Generation &
 * Impact scenario DOES declare unit structure, so
 * {@link blockCountsFromScenarioUnits} bridges it: when a scenario exists,
 * the check runs on real counts instead of the worst case.
 *
 * The output is advisory and never overrides the N-0 verdict. The signal
 * worth acting on is {@link PhaseContingencyAssessment.hiddenExposure}: N-0
 * passes, N-1 does not. That is the state that ships a plan nobody has
 * stress-tested.
 *
 * Pure and side-effect free. MW throughout; no solver physics is recomputed.
 */
import type { PhasePowerComponentKey, ProjectIntent, ProjectPhase } from '../types/siteRag';
import { PHASE_POWER_COMPONENT_LABEL } from '../types/siteRag';
import type {
  PhaseFeasibility,
  PhaseFeasibilityPerSource,
  PhaseFeasibilityVerdict,
} from './phaseFeasibility';
import {
  assessPhaseFeasibility,
  firmCapacitySiteContextFromIntent,
} from './phaseFeasibility';
import { effectivePhases } from './projectIntent';
import { generationProjectInputHash } from './generationImpact/generationProjectInputSync';
import { generationImpactScenarioIntegrityReason } from './generationImpact/integrity';
import type { GenerationImpactScenarioSnapshot } from '../types/generationImpact';

/** Numeric tolerance for the >= comparisons (MW). */
const TOL_MW = 1e-6;

const round1 = (mw: number): number => Math.round(mw * 10) / 10;

export interface PhaseContingencyBlock {
  component: PhasePowerComponentKey;
  /** Firm MW this source contributes with everything running (N-0). */
  firmMW: number;
  /** Independent blocks the capacity is assumed or declared to sit in. */
  blockCount: number;
  /** Firm MW lost when the largest single block of this source trips. */
  largestBlockFirmMW: number;
  /** True when `blockCount` was assumed (worst case), not declared. */
  blockCountAssumed: boolean;
}

export interface PhaseContingencyAssessment {
  criterion: 'N-1';
  /** Firm MW with everything running; mirrors the N-0 grade. */
  firmDeliverableMW: number;
  /** The single largest block across the phase mix. */
  largestContingencyMW: number;
  largestContingencyComponent: PhasePowerComponentKey | null;
  /** Firm MW surviving the loss of that block. */
  firmAfterN1MW: number;
  requiredMW: number;
  requiredFirmMW: number;
  /** Survives N-1 against the bare load. */
  n1CoversLoad: boolean;
  /** Survives N-1 against load + planning reserve: the binding test. */
  n1Adequate: boolean;
  verdict: PhaseFeasibilityVerdict;
  /**
   * The actionable signal: the N-0 grade passes while N-1 does not, so the
   * phase reads adequate today and is not stress-tested.
   */
  hiddenExposure: boolean;
  blocks: PhaseContingencyBlock[];
  /** True when ANY row used an assumed block count. */
  assumesWorstCase: boolean;
  /** Complete, print-ready sentence. */
  statement: string;
}

/** Declared block counts, keyed by phase component. */
export type PhaseBlockCounts = Partial<Record<PhasePowerComponentKey, number>>;

/**
 * Canonical report-level N-1 screen for the ultimate declared phase.
 *
 * The Project Intent phase model does not declare equipment-unit counts. It
 * does, however, declare named source groups (grid, gas turbines, recips,
 * etc.) and their cumulative exact MW. This screen therefore removes the
 * largest capacity-credit-weighted DECLARED SOURCE GROUP. It must never infer
 * a unit size from a technology catalog or from free-form report prose.
 */
export interface DeclaredIntentFirmCapacityN1 {
  phaseId: string;
  phaseLabel: string;
  phaseSequence: number;
  basis: 'declared-source-groups';
  firmDeliverableN0MW: number;
  largestContingencyMW: number;
  largestContingencyComponent: PhasePowerComponentKey;
  firmDeliverableN1MW: number;
  requiredFirmMW: number;
  shortfallMW: number;
  verdict: 'holds' | 'shortfall';
}

const asCount = (value: number | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) && value >= 1
    ? Math.floor(value)
    : null;

/**
 * Resolve the N-1 picture for one phase from its N-0 feasibility grade.
 *
 * Sources with zero firm contribution (storage, unsized entries) cannot be
 * the binding contingency and never become one. Every other source is a
 * candidate; the largest single block across them all is the contingency.
 */
export function assessPhaseContingency(
  feasibility: PhaseFeasibility,
  blockCounts: PhaseBlockCounts = {},
): PhaseContingencyAssessment {
  const blocks: PhaseContingencyBlock[] = feasibility.perSource
    .filter((source: PhaseFeasibilityPerSource) => source.firmMW > TOL_MW)
    .map((source: PhaseFeasibilityPerSource) => {
      const declared = asCount(blockCounts[source.component]);
      const blockCount = declared ?? 1;
      return {
        component: source.component,
        firmMW: source.firmMW,
        blockCount,
        largestBlockFirmMW: source.firmMW / blockCount,
        blockCountAssumed: declared == null,
      };
    });

  const firmDeliverableMW = feasibility.firmDeliverableMW;
  const largest = blocks.reduce<PhaseContingencyBlock | null>(
    (worst, block) => (worst == null || block.largestBlockFirmMW > worst.largestBlockFirmMW ? block : worst),
    null,
  );
  const largestContingencyMW = largest?.largestBlockFirmMW ?? 0;
  const firmAfterN1MW = Math.max(0, firmDeliverableMW - largestContingencyMW);

  const { requiredMW, requiredFirmMW } = feasibility;
  const n1CoversLoad = firmAfterN1MW + TOL_MW >= requiredMW;
  const n1Adequate = firmAfterN1MW + TOL_MW >= requiredFirmMW;
  const verdict: PhaseFeasibilityVerdict = n1Adequate
    ? 'feasible'
    : n1CoversLoad
      ? 'marginal'
      : 'infeasible';
  const hiddenExposure = feasibility.firmAdequate && !n1Adequate;
  const assumesWorstCase = blocks.some((block) => block.blockCountAssumed);

  const label = largest ? PHASE_POWER_COMPONENT_LABEL[largest.component] : null;
  const singleBlock = largest?.blockCount === 1;
  const head = largest == null
    ? `No firm source in this phase carries capacity, so there is nothing to lose at N-1.`
    : `Losing the largest block (${label}, ${round1(largestContingencyMW)} MW firm`
      + `${singleBlock ? ', the whole entry' : ` of ${round1(largest.firmMW)} MW`}) leaves `
      + `${round1(firmAfterN1MW)} MW against a ${round1(requiredFirmMW)} MW reserve-padded requirement.`;
  const grade = n1Adequate
    ? ' The phase survives N-1.'
    : n1CoversLoad
      ? ' The phase covers bare load at N-1 but not the reserve margin.'
      : ' The phase does not survive N-1.';
  const exposure = hiddenExposure
    ? ' It grades adequate with everything running, so this exposure is not visible in the N-0 check.'
    : '';
  const caveat = assumesWorstCase
    ? ' Block counts are not declared in the phase plan; this assumes the worst case of one block per source. '
      + 'Declare the machine count (or promote a Generation & Impact scenario) to refine it.'
    : '';

  return {
    criterion: 'N-1',
    firmDeliverableMW,
    largestContingencyMW,
    largestContingencyComponent: largest?.component ?? null,
    firmAfterN1MW,
    requiredMW,
    requiredFirmMW,
    n1CoversLoad,
    n1Adequate,
    verdict,
    hiddenExposure,
    blocks,
    assumesWorstCase,
    statement: `${head}${grade}${exposure}${caveat}`,
  };
}

function hasOnlyExactDeclaredSourceGroups(phase: ProjectPhase): boolean {
  const cfg = phase.powerSourceConfig;
  if (!cfg || cfg.mixMode !== 'and' || cfg.components.length === 0) return false;
  if (new Set(cfg.components).size !== cfg.components.length) return false;
  // Battery nameplate MW alone does not declare the MWh/duration needed to
  // sustain a 24/7 N-1 duty. The phase grader's 95% credit is explicitly only
  // within its discharge window, so a report-level firm screen must withhold.
  if (cfg.components.includes('battery')) return false;
  return cfg.components.every((component) => {
    const capacityMW = cfg.componentParams?.[component]?.capacityMW;
    return typeof capacityMW === 'number' && Number.isFinite(capacityMW) && capacityMW > 0;
  });
}

/**
 * Resolve the N-1 field used by `powerAnalysis.firmCapacityN1`.
 *
 * Scope is intentionally strict:
 *  - only the FINAL (highest-sequence) phase represents the report's ultimate
 *    project duty;
 *  - the phase must explicitly be an AND-combined mix;
 *  - every selected source group must carry an exact positive MW declaration;
 *  - a standalone battery group is not admitted from MW alone (duration/MWh
 *    is not represented in Project Intent);
 *  - each declared source group is one contingency group because Project
 *    Intent carries no unit-count field.
 *
 * If any condition is missing, return null and omit the numeric N-1 contract.
 * In particular, `technology.typicalModuleMWe` is never consulted.
 */
export function assessDeclaredIntentFirmCapacityN1(
  intent: ProjectIntent | undefined | null,
): DeclaredIntentFirmCapacityN1 | null {
  const phases = (intent?.phases ?? []).slice().sort((a, b) => a.sequence - b.sequence);
  const phase = phases[phases.length - 1];
  if (!phase || !hasOnlyExactDeclaredSourceGroups(phase)) return null;

  const feasibility = assessPhaseFeasibility(
    phase,
    firmCapacitySiteContextFromIntent(intent),
  );
  if (!feasibility || feasibility.mixMode !== 'and') return null;
  if (
    feasibility.perSource.length !== phase.powerSourceConfig!.components.length
    || feasibility.perSource.some((source) => source.capacityBasis !== 'declared-exact')
  ) {
    return null;
  }

  // Passing an explicit count of one records the semantic actually used here:
  // one declared source group is one contingency group. It does not claim the
  // group contains a single physical machine.
  const declaredGroups = Object.fromEntries(
    phase.powerSourceConfig!.components.map((component) => [component, 1]),
  ) as PhaseBlockCounts;
  const assessment = assessPhaseContingency(feasibility, declaredGroups);
  if (!assessment.largestContingencyComponent || assessment.largestContingencyMW <= TOL_MW) {
    return null;
  }

  const firmDeliverableN0MW = round1(assessment.firmDeliverableMW);
  const largestContingencyMW = round1(assessment.largestContingencyMW);
  const firmDeliverableN1MW = round1(assessment.firmAfterN1MW);
  const requiredFirmMW = round1(assessment.requiredFirmMW);
  const shortfallMW = round1(Math.max(0, assessment.requiredFirmMW - assessment.firmAfterN1MW));

  return {
    phaseId: phase.id,
    phaseLabel: phase.label?.trim() || `Phase ${phase.sequence}`,
    phaseSequence: phase.sequence,
    basis: 'declared-source-groups',
    firmDeliverableN0MW,
    largestContingencyMW,
    largestContingencyComponent: assessment.largestContingencyComponent,
    firmDeliverableN1MW,
    requiredFirmMW,
    shortfallMW,
    verdict: assessment.n1Adequate ? 'holds' : 'shortfall',
  };
}

/** Minimal frozen-unit shape the scenario bridge needs. */
export interface ScenarioUnitLike {
  technology?: string;
  count?: number;
}

/**
 * Bridge a promoted scenario's unit structure to phase block counts, so the
 * N-1 check runs on DECLARED machines rather than the worst case. Unknown
 * technologies are ignored rather than guessed.
 */
export function blockCountsFromScenarioUnits(
  units: readonly ScenarioUnitLike[],
): PhaseBlockCounts {
  const counts: PhaseBlockCounts = {};
  for (const unit of units) {
    const key: PhasePowerComponentKey | null =
      unit.technology === 'reciprocating-engine' ? 'recip'
        : unit.technology === 'simple-cycle-gas-turbine'
          || unit.technology === 'combined-cycle-gas-turbine' ? 'gas-turbine'
          : unit.technology === 'grid-import' ? 'grid'
            : null;
    if (!key) continue;
    const count = asCount(unit.count) ?? 1;
    counts[key] = (counts[key] ?? 0) + count;
  }
  return counts;
}

export const MODELLED_GRID_SUPPLY_QUALIFICATION =
  'Aggregate feed capacity is a supply envelope, not on-site generation or firm utility service. '
  + 'The modeled contingency result assumes independent feed losses; shared utility, substation, protection, '
  + 'route and other common-cause failures remain unqualified. Confirm service and independence with the utility.';

export interface SolvedScenarioPhaseContingency {
  phaseId: string;
  normalNetMW: number;
  contingencyLostMW: number;
  contingencyNetMW: number;
  reserveRequiredMW: number;
  reserveMarginMW: number;
  screenVerdict: 'pass' | 'fail' | 'conditional' | 'indeterminate';
  capacityRole?: 'grid-supply' | 'on-site-generation';
  qualificationNote?: string | null;
}

/**
 * When a promoted Generation & Impact scenario is bound to a specific phase
 * (or the sole phase of a single-phase intent with verified scenario stamps)
 * and its N-1 screen has been solved in `generationImpactProjection.capacityAndResilience`,
 * return that authoritative same-phase N-1 result so phase capability charts and
 * derived phasing risks do not contradict the promoted scenario with a worst-case
 * single-block assumption.
 *
 * Fails closed when any numeric input is non-finite or out of bounds, when
 * scenario/projection provenance or target phase/load basis is mismatched or
 * stale, or when `screenVerdict === 'pass'` contradicts surviving N-1 capacity.
 */
export function resolvePromotedScenarioPhaseContingency(
  report: {
    projectIntentAtGen?: ProjectIntent | null;
    generationImpactScenarioAtGen?: {
      id?: string | null;
      siteId?: string | null;
      name?: string | null;
      targetPhaseId?: string | null;
      inputHash?: string | null;
      resultHash?: string | null;
      sharedProjectInputHash?: string | null;
      promotedAt?: string | null;
      integrity?: { valid?: boolean } | null;
      input?: {
        facilityLoadMW?: number;
        units?: readonly ScenarioUnitLike[];
      } | null;
      result?: unknown;
    } | null;
    generationImpactProjection?: {
      source?: {
        scenarioId?: string | null;
        scenarioName?: string | null;
        targetPhaseId?: string | null;
        inputHash?: string | null;
        resultHash?: string | null;
        promotedAt?: string | null;
        integrity?: { valid?: boolean } | null;
      } | null;
      capacityAndResilience?: {
        capacityRole?: 'grid-supply' | 'on-site-generation';
        qualificationNote?: string | null;
        criterion?: string;
        facilityLoadMW?: number;
        criticalLoadMW?: number;
        reserveMarginPct?: number;
        normalNetMW?: number;
        contingencyLostMW?: number;
        contingencyNetMW?: number;
        loadMarginMW?: number;
        reserveRequiredMW?: number;
        reserveMarginMW?: number;
        loadVerdict?: 'pass' | 'fail' | 'conditional' | 'indeterminate';
        reserveVerdict?: 'pass' | 'fail' | 'conditional' | 'indeterminate';
        screenVerdict?: 'pass' | 'fail' | 'conditional' | 'indeterminate';
      } | null;
    } | null;
  } | null | undefined,
): SolvedScenarioPhaseContingency | null {
  const intent = report?.projectIntentAtGen;
  const capRes = report?.generationImpactProjection?.capacityAndResilience;
  if (!intent || !capRes || capRes.criterion !== 'N-1') return null;

  // 1. Validate numeric fields with Number.isFinite and non-negative bounds.
  if (
    typeof capRes.normalNetMW !== 'number'
    || !Number.isFinite(capRes.normalNetMW)
    || capRes.normalNetMW < 0
    || typeof capRes.contingencyNetMW !== 'number'
    || !Number.isFinite(capRes.contingencyNetMW)
    || capRes.contingencyNetMW < 0
    || typeof capRes.reserveRequiredMW !== 'number'
    || !Number.isFinite(capRes.reserveRequiredMW)
    || capRes.reserveRequiredMW <= 0
  ) {
    return null;
  }
  if (
    capRes.contingencyLostMW != null
    && (typeof capRes.contingencyLostMW !== 'number'
      || !Number.isFinite(capRes.contingencyLostMW)
      || capRes.contingencyLostMW < 0)
  ) {
    return null;
  }
  if (
    capRes.reserveMarginMW != null
    && (typeof capRes.reserveMarginMW !== 'number' || !Number.isFinite(capRes.reserveMarginMW))
  ) {
    return null;
  }
  if (
    capRes.facilityLoadMW != null
    && (typeof capRes.facilityLoadMW !== 'number'
      || !Number.isFinite(capRes.facilityLoadMW)
      || capRes.facilityLoadMW <= 0)
  ) {
    return null;
  }
  if (
    capRes.criticalLoadMW != null
    && (typeof capRes.criticalLoadMW !== 'number'
      || !Number.isFinite(capRes.criticalLoadMW)
      || capRes.criticalLoadMW <= 0)
  ) {
    return null;
  }
  if (
    capRes.loadMarginMW != null
    && (typeof capRes.loadMarginMW !== 'number' || !Number.isFinite(capRes.loadMarginMW))
  ) {
    return null;
  }

  const contingencyLostMW =
    capRes.contingencyLostMW ?? (capRes.normalNetMW - capRes.contingencyNetMW);
  if (!Number.isFinite(contingencyLostMW) || contingencyLostMW < -TOL_MW) return null;
  if (capRes.contingencyNetMW > capRes.normalNetMW + TOL_MW) return null;
  if (Math.abs((capRes.normalNetMW - contingencyLostMW) - capRes.contingencyNetMW) > 0.15) {
    return null;
  }

  const reserveMarginMW =
    capRes.reserveMarginMW ?? (capRes.contingencyNetMW - capRes.reserveRequiredMW);
  if (!Number.isFinite(reserveMarginMW)) return null;
  if (Math.abs((capRes.contingencyNetMW - capRes.reserveRequiredMW) - reserveMarginMW) > 0.15) {
    return null;
  }

  // 2. Verify scenario / projection integrity and provenance stamps against projectIntentAtGen (fail-closed).
  const scenario = report?.generationImpactScenarioAtGen;
  const projSource = report?.generationImpactProjection?.source;
  if (!scenario || !projSource) {
    return null;
  }
  if (scenario.integrity?.valid === false || projSource.integrity?.valid === false) {
    return null;
  }
  const hasFullPersistedSnapshot = Boolean(
    scenario.input
      && scenario.result
      && typeof scenario.siteId === 'string'
      && scenario.siteId.trim()
      && typeof scenario.name === 'string'
      && scenario.name.trim(),
  );
  if (hasFullPersistedSnapshot) {
    if (
      generationImpactScenarioIntegrityReason(
        scenario as unknown as GenerationImpactScenarioSnapshot,
      ) !== null
    ) {
      return null;
    }
  } else if (scenario.integrity?.valid !== true) {
    return null;
  }

  const scenarioId = typeof scenario.id === 'string' ? scenario.id.trim() : '';
  const projScenarioId = typeof projSource.scenarioId === 'string' ? projSource.scenarioId.trim() : '';
  const intentScenarioId =
    typeof intent.promotedGenerationScenarioId === 'string'
      ? intent.promotedGenerationScenarioId.trim()
      : '';
  if (
    !scenarioId
    || !projScenarioId
    || !intentScenarioId
    || scenarioId !== projScenarioId
    || scenarioId !== intentScenarioId
  ) {
    return null;
  }

  const inputHash = typeof scenario.inputHash === 'string' ? scenario.inputHash.trim() : '';
  const projInputHash = typeof projSource.inputHash === 'string' ? projSource.inputHash.trim() : '';
  const intentInputHash =
    typeof intent.promotedGenerationInputHash === 'string'
      ? intent.promotedGenerationInputHash.trim()
      : '';
  if (
    !inputHash
    || !projInputHash
    || !intentInputHash
    || inputHash !== projInputHash
    || inputHash !== intentInputHash
  ) {
    return null;
  }

  const resultHash = typeof scenario.resultHash === 'string' ? scenario.resultHash.trim() : '';
  const projResultHash = typeof projSource.resultHash === 'string' ? projSource.resultHash.trim() : '';
  const intentResultHash =
    typeof intent.promotedGenerationResultHash === 'string'
      ? intent.promotedGenerationResultHash.trim()
      : '';
  if (
    !resultHash
    || !projResultHash
    || !intentResultHash
    || resultHash !== projResultHash
    || resultHash !== intentResultHash
  ) {
    return null;
  }

  const scenarioPromotedAt =
    typeof scenario.promotedAt === 'string' ? scenario.promotedAt.trim() : '';
  const projPromotedAt =
    typeof projSource.promotedAt === 'string' ? projSource.promotedAt.trim() : '';
  const intentPromotedAt =
    typeof intent.promotedGenerationPromotedAt === 'string'
      ? intent.promotedGenerationPromotedAt.trim()
      : '';
  if (scenarioPromotedAt || projPromotedAt || intentPromotedAt) {
    if (
      !scenarioPromotedAt
      || !projPromotedAt
      || scenarioPromotedAt !== projPromotedAt
      || (intentPromotedAt && intentPromotedAt !== scenarioPromotedAt)
      || (!hasFullPersistedSnapshot && !intentPromotedAt)
    ) {
      return null;
    }
  }

  if (typeof scenario.sharedProjectInputHash === 'string' && scenario.sharedProjectInputHash.trim()) {
    try {
      if (scenario.sharedProjectInputHash.trim() !== generationProjectInputHash(intent)) {
        return null;
      }
    } catch {
      return null;
    }
  }

  // 3. Verify explicit phase binding (or provenance-stamped single-phase binding) and load/reserve basis.
  const view = effectivePhases(intent);
  const explicitPhaseId =
    typeof scenario.targetPhaseId === 'string' ? scenario.targetPhaseId.trim() : '';
  const projTargetPhaseId =
    typeof projSource.targetPhaseId === 'string' ? projSource.targetPhaseId.trim() : '';
  if ('targetPhaseId' in scenario && !explicitPhaseId) {
    return null;
  }
  if (projTargetPhaseId && explicitPhaseId && projTargetPhaseId !== explicitPhaseId) {
    return null;
  }
  const phaseId =
    explicitPhaseId
    || projTargetPhaseId
    || (!view.isPhased && view.phases.length === 1 ? view.phases[0]?.id : undefined);
  if (!phaseId) return null;

  const targetPhase = view.phases.find((p) => p.id === phaseId);
  if (!targetPhase) return null;

  const phaseFeasibility = assessPhaseFeasibility(
    targetPhase,
    firmCapacitySiteContextFromIntent(intent),
  );
  if (!phaseFeasibility) return null;

  const scenarioFacilityMW =
    capRes.facilityLoadMW ?? scenario?.input?.facilityLoadMW ?? null;
  if (scenarioFacilityMW != null) {
    if (
      typeof scenarioFacilityMW !== 'number'
      || !Number.isFinite(scenarioFacilityMW)
      || scenarioFacilityMW <= 0
    ) {
      return null;
    }
    if (Math.abs(scenarioFacilityMW - phaseFeasibility.requiredMW) > 0.5) {
      return null;
    }
  }
  if (
    capRes.criticalLoadMW != null
    && capRes.reserveMarginPct != null
    && Number.isFinite(capRes.criticalLoadMW)
    && Number.isFinite(capRes.reserveMarginPct)
    && capRes.reserveMarginPct >= 0
  ) {
    const expectedReserveMW = capRes.criticalLoadMW * (1 + capRes.reserveMarginPct / 100);
    if (Math.abs(capRes.reserveRequiredMW - expectedReserveMW) > 0.15) {
      return null;
    }
    const basisLoadMW = scenarioFacilityMW ?? capRes.criticalLoadMW;
    if (Math.abs(basisLoadMW - phaseFeasibility.requiredMW) > 0.5) {
      return null;
    }
  } else if (Math.abs(capRes.reserveRequiredMW - phaseFeasibility.requiredFirmMW) > 0.5) {
    return null;
  }

  // 4. Require screenVerdict === 'pass' to be consistent with surviving N-1 capacity.
  const validVerdicts = new Set(['pass', 'fail', 'conditional', 'indeterminate']);
  if (capRes.screenVerdict != null && !validVerdicts.has(capRes.screenVerdict)) {
    return null;
  }
  const requiredLoadMW = scenarioFacilityMW ?? phaseFeasibility.requiredMW;
  const coversLoad = capRes.contingencyNetMW + TOL_MW >= requiredLoadMW;
  const coversReserve =
    capRes.contingencyNetMW + TOL_MW >= capRes.reserveRequiredMW
    && capRes.contingencyNetMW + TOL_MW >= phaseFeasibility.requiredFirmMW
    && reserveMarginMW >= -TOL_MW;

  if (capRes.screenVerdict === 'pass') {
    if (
      !coversLoad
      || !coversReserve
      || capRes.loadVerdict === 'fail'
      || capRes.reserveVerdict === 'fail'
      || capRes.reserveVerdict === 'conditional'
    ) {
      return null;
    }
  }

  const screenVerdict: SolvedScenarioPhaseContingency['screenVerdict'] =
    capRes.screenVerdict
    ?? (coversLoad && coversReserve ? 'pass' : coversLoad ? 'conditional' : 'fail');

  // 5. Preserve explicit qualification of modelled grid-feed assumptions as unverified,
  //    including mixed grid-plus-engine scenarios and any grid-dependent phase.
  const units = scenario.input?.units;
  const hasGridImportUnit =
    Array.isArray(units)
    && units.some(
      (u) =>
        u?.technology === 'grid-import'
        && (u.count == null || (typeof u.count === 'number' && Number.isFinite(u.count) && u.count > 0)),
    );
  const isPureGridSupply =
    capRes.capacityRole === 'grid-supply'
    || (Array.isArray(units)
      && units.length > 0
      && units.every(
        (u) =>
          u?.technology === 'grid-import'
          && (u.count == null || (typeof u.count === 'number' && Number.isFinite(u.count) && u.count > 0)),
      ));
  const phaseIncludesGrid =
    targetPhase.powerSource === 'grid'
    || Boolean(targetPhase.powerSourceConfig?.components.includes('grid'));
  const gridDependent = isPureGridSupply || hasGridImportUnit || phaseIncludesGrid;
  const capacityRole: SolvedScenarioPhaseContingency['capacityRole'] =
    isPureGridSupply ? 'grid-supply' : 'on-site-generation';
  const qualificationNote = gridDependent
    ? capRes.qualificationNote?.trim() || MODELLED_GRID_SUPPLY_QUALIFICATION
    : capRes.qualificationNote?.trim() || null;

  return {
    phaseId,
    normalNetMW: capRes.normalNetMW,
    contingencyLostMW: Math.max(0, contingencyLostMW),
    contingencyNetMW: capRes.contingencyNetMW,
    reserveRequiredMW: capRes.reserveRequiredMW,
    reserveMarginMW,
    screenVerdict,
    capacityRole,
    qualificationNote,
  };
}
